import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import BackToTop from '../../components/BackToTop';
import { Sparkles, TrendingUp, Target, Shield, Infinity as InfinityIcon, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { useScrollTracking } from '../../hooks/useScrollTracking';
import { useClickTracking } from '../../hooks/useClickTracking';
import { ServiceSchema, FAQSchema, BreadcrumbSchema } from '../../components/JsonLd';

// Investment component - simplified without expandable details
const InvestmentBlock = ({ anchor }) => {
  return (
    <div className="mt-md pt-md border-t border-mist">
      <p className="text-sm sm:text-base text-slate font-normal">
        {anchor}
      </p>
    </div>
  );
};

const PartnershipsPage = () => {
  useSEO({
    title: 'Partnerships',
    description: 'Culture Catalyst Workshops, Leadership Accelerator Programs, Operational Excellence Projects, and Executive Advisory. Custom engagements for healthcare institutions committed to transformation.',
  });

  useScrollTracking('partnerships');
  useClickTracking('partnerships');

  // First 4 service cards (2x2 grid) with outcome-first storytelling
  const services = [
    {
      id: 'culture-catalyst',
      icon: Sparkles,
      name: 'Culture Catalyst Workshop',
      outcomeStory: 'When a 200-bed hospital saw employee engagement scores rise 34% in six months, it wasn\'t from a new policy—it was from teams learning to see each other differently.',
      subtitle: 'For institutions committed to igniting engagement and improving psychological safety',
      description: 'Half-day or full-day interactive workshop blending expressive arts with evidence-based frameworks.',
      deliverables: 'Includes: pre-assessment, facilitated session, recommendations, follow-up',
      investmentAnchor: 'Investment shared following consultation to ensure appropriate scope and institutional alignment.',
      investmentDetails: null
    },
    {
      id: 'leadership-accelerator',
      icon: TrendingUp,
      name: 'Leadership Accelerator Program',
      outcomeStory: 'A cohort of 12 clinical leaders transformed from "managing tasks" to "inspiring teams"—their direct reports\' satisfaction scores increased 28% while turnover dropped by half.',
      subtitle: 'For institutions investing in next-level leadership',
      description: '6-week cohort-based program combining leadership development, coaching, and applied learning sciences.',
      deliverables: 'Includes: six sessions, assessments, personalized coaching, impact report',
      investmentAnchor: 'Investment shared following consultation to ensure appropriate scope and institutional alignment.',
      investmentDetails: null
    },
    {
      id: 'operational-excellence',
      icon: Target,
      name: 'Operational Excellence Project',
      outcomeStory: 'Reduced NICU chest X-rays by 40% while improving safety outcomes—proving that better processes don\'t just cut costs, they enhance care.',
      subtitle: 'For institutions committed to process improvements and measurable ROI',
      description: '8–12 week engagement using improvement science to address operational and process bottlenecks.',
      deliverables: 'Includes: process mapping, analysis, solution design, implementation support',
      investmentAnchor: 'Investment shared following consultation to ensure appropriate scope and institutional alignment.',
      investmentDetails: null
    },
    {
      id: 'culture-assessment',
      icon: Shield,
      name: 'Culture Assessment',
      outcomeStory: 'Few healthcare leaders understand what their teams actually experience until they see the data—one assessment revealed communication gaps that, once addressed, reduced incident reports by 22%.',
      subtitle: 'For institutions committed to actionable understanding of workplace culture',
      description: 'Comprehensive assessment using validated surveys, interviews, and focus groups.',
      deliverables: 'Includes: survey administration, analysis, leadership debrief, roadmap',
      investmentAnchor: 'Investment shared following consultation to ensure appropriate scope and institutional alignment.',
      investmentDetails: null
    }
  ];

  const allPackagesInclude = [
    {
      title: 'Discovery Call',
      description: 'Initial needs assessment and goal alignment'
    },
    {
      title: 'Customization',
      description: 'Tailored to your context and objectives'
    },
    {
      title: 'Impact Measurement',
      description: 'Post-engagement follow-up and outcome tracking'
    }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const breadcrumbItems = [
    { name: 'Home', url: 'https://thepoeticmethod.com/' },
    { name: 'Partnerships', url: 'https://thepoeticmethod.com/partnerships' },
  ];

  return (
    <>
      <ServiceSchema />
      <FAQSchema />
      <BreadcrumbSchema items={breadcrumbItems} />
      <div className="min-h-screen flex flex-col bg-cream">
        <Header />
      <main className="flex-1 pt-20 sm:pt-24 md:pt-28">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-md lg:px-xl py-2xl" aria-labelledby="services-hero-heading">
          <motion.h1 
            id="services-hero-heading"
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-navy mb-md" 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Strategic Partnerships Designed for Institutional Transformation
          </motion.h1>
          <motion.p 
            className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed max-w-4xl mb-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            The Poetic Method™ blends evidence-based practice with expressive arts and poetry-informed methodology. 
            Every service offering unlocks human potential through the intersection of healthcare expertise and creative 
            insight—delivering measurable outcomes with warmth and depth.
          </motion.p>
        </section>

        {/* Flagship Offering: Executive Advisory */}
        <section className="py-2xl bg-gradient-to-br from-coral via-coral/90 to-gold/80" aria-labelledby="executive-advisory-heading">
          <div className="max-w-6xl mx-auto px-md lg:px-xl">
            <motion.article
              className="text-white space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="max-w-3xl mx-auto gap-lg flex flex-col">
                <div>
                  <Sparkles size={28} className="text-white/90" aria-hidden="true" />
                </div>
                
                <h2 id="executive-advisory-heading" className="font-display text-2xl sm:text-3xl md:text-4xl text-white font-semibold leading-tight">
                  The Poetic Method™ Executive Advisory
                </h2>
                
                <p className="font-body text-base sm:text-lg text-white/90 leading-relaxed">
                  A high-trust thought partnership for senior leaders who must tell the truth, hold complexity, and remain anchored while navigating institutional responsibility.
                </p>
              </div>

              <div className="max-w-5xl mx-auto">
                {/* Two Column Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-lg items-start">
                  <div className="max-w-md">
                    <h3 className="font-display text-lg sm:text-xl text-white font-semibold">What It Is</h3>
                    <ul className="mt-3 space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">A limited-capacity executive advisory relationship intentionally designed for depth and discretion</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">Created for leaders operating within complex systems (healthcare, public institutions, education)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">Focused on clarity, coherence, and principled decision-making in high-stakes environments</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">Distinct from coaching or facilitation—this is strategic reflection and institutional discernment</span>
                      </li>
                    </ul>
                  </div>

                  <div className="max-w-md">
                    <h3 className="font-display text-lg sm:text-xl text-white font-semibold">How It Works</h3>
                    <ul className="mt-3 space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">Private 1:1 advisory sessions (monthly or bi-monthly)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">Optional light reflective preparation to focus inquiry and deepen dialogue</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">•</span>
                        <span className="font-body text-sm sm:text-base text-white/90 leading-relaxed">Structured thought partnership centered on:</span>
                      </li>
                    </ul>
                    <ul className="ml-6 space-y-2.5 mt-2.5">
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5 flex-shrink-0" aria-hidden="true">◦</span>
                        <span className="font-body text-sm sm:text-base text-white leading-relaxed">Decision-making under pressure</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5" aria-hidden="true">◦</span>
                        <span className="font-body text-sm sm:text-base text-white leading-relaxed">Moral clarity within institutional constraints</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5" aria-hidden="true">◦</span>
                        <span className="font-body text-sm sm:text-base text-white leading-relaxed">Leading without fragmentation</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5" aria-hidden="true">◦</span>
                        <span className="font-body text-sm sm:text-base text-white leading-relaxed">Designing learning as infrastructure</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-white/80 mt-1.5" aria-hidden="true">◦</span>
                        <span className="font-body text-sm sm:text-base text-white leading-relaxed">Operationalizing coherence across systems</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Full Width Description Below */}
                <div className="mt-lg gap-md flex flex-col">
                  <p className="font-body text-sm sm:text-base text-white/90 leading-relaxed pt-md border-t border-white/20">
                    The Executive Advisory is the flagship offering of The Poetic Method™—a deeply personal, intentionally limited space where disciplined strategy and human insight converge to strengthen leadership at the highest levels.
                  </p>

                  <div className="flex flex-col gap-md sm:flex-row sm:items-center sm:justify-between pt-md border-t border-white/20">
                    <p className="font-body text-xs sm:text-sm text-white/80 max-w-md">
                      Limited engagements annually. Investment shared upon inquiry.
                    </p>
                    
                    <Link
                      to="/contact"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-coral px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:bg-white/90 hover:shadow-lg"
                    >
                      Inquire About Advisory
                      <ArrowRight size={18} aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.article>
          </div>
        </section>

        {/* Services Grid - 2x2 */}
        <section className="py-xl lg:py-2xl" aria-label="Service offerings">
          <div className="max-w-6xl mx-auto px-md lg:px-xl">
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-lg"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {services?.map((service, index) => {
                const IconComponent = service?.icon;
                return (
                  <motion.article
                    key={service?.id}
                    className="bg-white rounded-xl border border-mist p-lg xl:p-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-l-[3px] hover:border-l-teal"
                    style={{
                      boxShadow: '0 2px 8px rgba(26, 58, 82, 0.06)'
                    }}
                    variants={fadeInUp}
                    aria-labelledby={`service-${service?.id}-title`}
                  >
                    {/* Icon - Teal, 24px, decorative */}
                    {IconComponent && (
                      <div className="mb-md">
                        <IconComponent size={24} className="text-teal" aria-hidden="true" />
                      </div>
                    )}
                    {/* Title - Playfair Display, navy, 1.25rem, semibold */}
                    <h3
                      id={`service-${service?.id}-title`}
                      className="font-display text-lg sm:text-xl text-navy font-semibold mb-sm leading-tight"
                    >
                      {service?.name}
                    </h3>
                    {/* Subtitle - Source Sans 3, slate, 0.875rem, regular */}
                    <p className="font-body text-xs sm:text-sm text-slate mb-md">
                      {service?.subtitle}
                    </p>
                    {/* Description - Source Sans 3, charcoal, 1rem, 2-3 lines max */}
                    <p className="font-body text-sm sm:text-base text-charcoal mb-md leading-relaxed">
                      {service?.description}
                    </p>
                    {/* Deliverables - Source Sans 3, slate, 0.875rem, single line */}
                    <p className="font-body text-xs sm:text-sm text-slate mb-sm">
                      {service?.deliverables}
                    </p>
                    {/* Investment - Source Sans 3, slate, 1rem, regular weight */}
                    <InvestmentBlock 
                      anchor={service?.investmentAnchor}
                    />
                  </motion.article>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Flagship Card: Transformation Partnership */}
        <section className="py-10 sm:py-12 md:py-16" aria-labelledby="transformation-partnership-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
            <motion.article
              className="bg-navy rounded-xl p-6 sm:p-8 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              aria-labelledby="transformation-partnership-heading"
            >
              {/* Icon: White for visibility */}
              <div className="mb-4">
                <InfinityIcon size={28} className="text-white/90 sm:w-8 sm:h-8" aria-hidden="true" />
              </div>
              
              {/* Title: WHITE, not teal - high contrast */}
              <h3 id="transformation-partnership-heading" className="font-display text-xl sm:text-2xl md:text-3xl text-white font-semibold mb-3">
                Transformation Partnership
              </h3>
              
              {/* Subtitle: Light cream/white for readability */}
              <p className="font-body text-sm sm:text-base md:text-lg text-white/70 mb-5 sm:mb-6">
                For institutions committed to sustained, enterprise-wide change
              </p>
              
              <p className="font-body text-sm sm:text-base md:text-lg text-white/90 mb-4 leading-relaxed">
                Ongoing partnership including quarterly workshops, executive coaching, operational projects, and culture assessments.
              </p>
              
              <p className="font-body text-xs sm:text-sm text-white/60 mb-5 sm:mb-6">
                Includes: custom annual plan, quarterly touchpoints, priority access
              </p>
              
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5 sm:gap-6 mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-white/20">
                <p className="font-body text-sm sm:text-base text-white/80">
                  Investment shared following consultation to ensure appropriate scope and institutional alignment.
                </p>
                
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-coral text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:bg-coral/90 hover:shadow-lg whitespace-nowrap"
                >
                  Inquire About Partnership
                  <ArrowRight size={18} aria-hidden="true" />
                </Link>
              </div>
            </motion.article>
          </div>
        </section>

        {/* All Packages Include Section */}
        <section className="py-16 md:py-20 bg-cream">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <motion.h2 
              className="font-display text-3xl md:text-4xl text-navy text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              All Packages Include
            </motion.h2>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {allPackagesInclude?.map((item, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  variants={fadeInUp}
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal/10 flex items-center justify-center">
                    <CheckCircle2 size={32} className="text-teal" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-xl text-navy font-semibold mb-2">
                    {item?.title}
                  </h3>
                  <p className="font-body text-base text-slate">
                    {item?.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Scripture Callout */}
        <section className="px-4 sm:px-6 md:px-12 lg:px-16 py-12 sm:py-16 md:py-20 bg-gradient-to-br from-navy/95 via-navy/90 to-teal/85" aria-label="Scripture reflection">
          <div className="max-w-4xl mx-auto">
            <motion.figure
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <blockquote className="font-display text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed italic">
                "For we are his workmanship, created in Christ Jesus for good works, which God prepared beforehand, that we should walk in them."
              </blockquote>
              <figcaption className="mt-6 sm:mt-8 font-body text-sm sm:text-base text-white/80">
                —Ephesians 2:10
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* Pricing Note Callout */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <motion.aside 
              className="border-l-4 border-coral bg-cream/50 p-6 rounded-r-lg"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-body text-sm text-slate">
                Engagements are custom-designed based on organizational scope, complexity, and leadership objectives.
              </p>
            </motion.aside>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
            <motion.h2 
              className="font-display text-3xl md:text-4xl text-navy mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Ready to Begin?
            </motion.h2>
            <motion.p 
              className="font-body text-lg text-charcoal mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Let's explore how The Poetic Method™ can support your organization's transformation. 
              Schedule a discovery call to discuss your needs and goals.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 bg-coral text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:bg-coral/90 hover:shadow-xl hover:scale-105"
              >
                Schedule Discovery Call
                <ArrowRight size={20} aria-hidden="true" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
};

export default PartnershipsPage;